import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "audit-logs",
    title: "บันทึกการใช้งาน (Audit logs)",
    summary:
      "ประวัติเหตุการณ์สำคัญทั้งหมดในระบบ: ใครทำอะไร กับใคร เมื่อไร จากที่ไหน — ทั้งการเข้าสู่ระบบของผู้ใช้และทุกการเปลี่ยนแปลงที่ผู้ดูแลทำ ใช้ตรวจสอบย้อนหลัง สืบสวนเหตุผิดปกติ และส่งออกเป็นหลักฐาน",
    access:
      "ต้องมีสิทธิ์ logs:read (บทบาท owner, admin และ auditor) หน้านี้อ่านอย่างเดียว ไม่มีใครแก้ไขหรือลบรายการทีละรายการได้ รายการเก่าจะถูกลบอัตโนมัติตามระยะเวลาที่ตั้งในหน้า “ระยะเวลาเก็บบันทึก”",
    shot: { file: "audit-logs-list.png", caption: "หน้าบันทึกการใช้งาน" },
    sections: [
      {
        id: "table",
        heading: "ตารางเหตุการณ์",
        body: [
          "รายการเรียงจากใหม่ไปเก่า แสดงทีละ 25 / 50 / 100 แถว (เลือกที่ “แถวต่อหน้า” ด้านล่าง) ใช้ปุ่มหน้าถัดไป / หน้าก่อน เพื่อเลื่อนดู ปุ่มโหลดใหม่ (ไอคอนลูกศรวน) ดึงเหตุการณ์ล่าสุด",
          "ตารางนี้ไม่มีช่องค้นหาข้อความอิสระ ให้ใช้ “ตัวกรอง” แทน (ดูหัวข้อถัดไป) และเรียงลำดับคอลัมน์ไม่ได้",
        ],
        fieldsTitle: "คอลัมน์ในตาราง",
        fields: [
          { name: "เวลา", description: "วันและเวลาที่เกิดเหตุการณ์ แสดงตามเขตเวลาของระบบ" },
          {
            name: "เหตุการณ์",
            description:
              "ชื่อประเภทเหตุการณ์ เช่น user.signed_in (ดูความหมายในหัวข้อ “ประเภทเหตุการณ์ที่พบบ่อย”)",
          },
          {
            name: "ผู้กระทำ",
            description:
              "รหัสของผู้ที่ทำให้เกิดเหตุการณ์: รหัสผู้ใช้ (UUID) ของผู้ดูแลหรือผู้ใช้เอง, “system” = ระบบหรือคำสั่งของผู้ดูแลเซิร์ฟเวอร์, “anonymous” = ผู้ที่ยังไม่ได้เข้าสู่ระบบ (เช่น พยายามเข้าสู่ระบบแต่ไม่สำเร็จ)",
          },
          {
            name: "ผู้ใช้",
            description:
              "รหัสผู้ใช้ที่ได้รับผลจากเหตุการณ์ (เช่น ผู้ใช้ที่ถูกระงับ) ว่างถ้าเหตุการณ์ไม่เกี่ยวกับผู้ใช้คนใด",
          },
          { name: "IP", description: "หมายเลข IP ที่ส่งคำขอ (ถ้ามี)" },
          {
            name: "User agent",
            description:
              "ข้อมูลเบราว์เซอร์/อุปกรณ์ของผู้กระทำ ซ่อนไว้โดยค่าเริ่มต้น เปิดได้จากปุ่ม “คอลัมน์”",
          },
        ],
      },
      {
        id: "details",
        heading: "ดูรายละเอียดของเหตุการณ์",
        shot: { file: "audit-logs-details.png", caption: "แถวที่ขยายดูรายละเอียด" },
        body: [
          "กดลูกศร “แสดงรายละเอียด” ที่ต้นแถว เพื่อดูข้อมูลทั้งหมดของเหตุการณ์ ได้แก่ ผู้กระทำ (ประเภท รหัส IP เบราว์เซอร์) ผู้ใช้ที่เกี่ยวข้อง และข้อมูลเพิ่มเติมของเหตุการณ์ (data) เช่น ช่องที่ถูกแก้ไข บทบาทที่เพิ่ม/ลบ หรือ Client ID ของแอป",
          "ระบบไม่บันทึกรหัสผ่าน รหัส OTP token หรือ secret ลงในบันทึกเด็ดขาด อีเมลหรือเบอร์โทรที่พิมพ์ตอนเข้าสู่ระบบไม่สำเร็จจะเก็บเป็นค่าแฮช (emailHash / valueHash) แทนค่าจริง",
        ],
      },
      {
        id: "filters",
        heading: "กรองเหตุการณ์",
        shot: { file: "audit-logs-filter.png", caption: "แผงตัวกรองที่กรองตามประเภทเหตุการณ์" },
        body: [
          "กดปุ่ม “ตัวกรอง” ในแถบเครื่องมือ แล้วกด “เพิ่มตัวกรอง” เลือกคอลัมน์ เงื่อนไข และค่า ตัวกรองหลายรายการจะใช้ร่วมกันแบบ “และ” (ต้องตรงทุกข้อ) ตารางกลับไปหน้าแรกทุกครั้งที่เปลี่ยนตัวกรอง",
        ],
        fieldsTitle: "ตัวกรองที่ใช้ได้",
        fields: [
          {
            name: "เหตุการณ์ — เป็น / เป็นอย่างใดอย่างหนึ่ง",
            description:
              "เลือกประเภทเหตุการณ์หนึ่งรายการ หรือหลายรายการพร้อมกัน เช่น user.banned และ user.unbanned",
          },
          {
            name: "ผู้กระทำ — เท่ากับ",
            description: "วางรหัสผู้ใช้ของผู้กระทำ (หรือ system / anonymous) ต้องตรงทั้งค่า",
          },
          {
            name: "ผู้ใช้ — เท่ากับ",
            description: "วางรหัสผู้ใช้ที่ได้รับผล เพื่อดูทุกอย่างที่เกิดกับผู้ใช้คนนั้น",
          },
          {
            name: "เวลา — ตั้งแต่ / ก่อน",
            description: "กำหนดช่วงเวลา ใส่สองตัวกรองเพื่อได้ช่วง “ตั้งแต่ … ก่อน …”",
          },
        ],
        steps: [
          "ตัวอย่าง “ใครเปลี่ยนบทบาทของผู้ใช้ในเดือนนี้”: กด “ตัวกรอง” → “เพิ่มตัวกรอง”",
          "เลือกคอลัมน์ “เหตุการณ์” เงื่อนไข “เป็น” ค่า user.roles_changed",
          "เพิ่มตัวกรองอีกรายการ: คอลัมน์ “เวลา” เงื่อนไข “ตั้งแต่” แล้วเลือกวันที่ 1 ของเดือน",
          "ดูคอลัมน์ “ผู้กระทำ” (ผู้ดูแลที่เปลี่ยน) และขยายแถวเพื่อดูบทบาทที่เพิ่ม/ลบ",
        ],
        tips: [
          "รหัสผู้ใช้หาได้จากหน้าผู้ใช้ (อยู่ในที่อยู่หน้าเว็บของหน้ารายละเอียดผู้ใช้) หน้ารายละเอียดผู้ใช้ยังมีประวัติเหตุการณ์ของผู้ใช้คนนั้นให้ดูโดยตรง",
          "กด “ลบทั้งหมด” ในแผงตัวกรองเพื่อล้างตัวกรองทั้งหมด",
        ],
      },
      {
        id: "export",
        heading: "ส่งออกเป็น CSV",
        body: [
          "กดปุ่ม “ส่งออก” → “ดาวน์โหลดเป็น CSV” ระบบจะดาวน์โหลดทุกเหตุการณ์ที่ตรงกับตัวกรองปัจจุบัน (ไม่ใช่เฉพาะหน้าที่เห็น) สูงสุด 5,000 รายการ ชื่อไฟล์เป็น audit-logs-ปปปป-ดด-วว.csv เปิดได้ด้วย Excel หรือ Google Sheets",
          "คอลัมน์ในไฟล์: occurredAt (เวลาแบบสากล UTC), type, actorType, actorId, subjectUserId, ip, userAgent, data",
        ],
        tips: [
          "ถ้ามีเกิน 5,000 รายการ ให้แบ่งช่วงเวลาด้วยตัวกรอง “เวลา” แล้วส่งออกทีละช่วง",
          "เวลาในไฟล์เป็น UTC (เวลาไทย = UTC + 7 ชั่วโมง)",
        ],
        warnings: [
          "ไฟล์ที่ส่งออกมีรหัสผู้ใช้และ IP ซึ่งเป็นข้อมูลส่วนบุคคล — เก็บและส่งต่ออย่างระมัดระวังตาม PDPA",
        ],
      },
      {
        id: "event-types",
        heading: "ประเภทเหตุการณ์ที่พบบ่อย",
        body: ["ชื่อเหตุการณ์อยู่ในรูป “สิ่งที่เกี่ยวข้อง.การกระทำ” ความหมายของเหตุการณ์ที่พบบ่อย:"],
        fieldsTitle: "ผู้ใช้และการเข้าสู่ระบบ",
        fields: [
          {
            name: "user.signed_in",
            description: "เข้าสู่ระบบสำเร็จ (data บอกวิธี: password / phone_otp / social และใช้ 2FA หรือไม่)",
          },
          {
            name: "user.sign_in_failed",
            description: "เข้าสู่ระบบไม่สำเร็จ (เช่น รหัสผ่านผิด) ผู้กระทำเป็น anonymous",
          },
          { name: "user.signed_out", description: "ออกจากระบบ (session = เครื่องเดียว, all = ทุกเครื่อง)" },
          {
            name: "user.created / user.updated / user.deleted",
            description: "สร้าง / แก้ไข / ลบบัญชีผู้ใช้ (data ของ user.updated บอกช่องที่แก้)",
          },
          { name: "user.banned / user.unbanned", description: "ระงับ / ยกเลิกระงับบัญชี" },
          { name: "user.roles_changed", description: "บทบาทในคอนโซลถูกเพิ่มหรือลบ" },
          {
            name: "user.type_changed / user.groups_changed",
            description: "เปลี่ยนประเภทผู้ใช้ / เปลี่ยนกลุ่มที่สังกัด",
          },
          {
            name: "user.password_changed",
            description: "ผู้ใช้เปลี่ยนรหัสผ่านเอง หรือรีเซ็ตผ่านลิงก์ในอีเมล",
          },
          {
            name: "user.password_reset_requested",
            description: "มีการขอลิงก์รีเซ็ตรหัสผ่าน (โดยผู้ใช้หรือผู้ดูแล)",
          },
          {
            name: "user.password_set_by_admin",
            description: "ผู้ดูแลตั้งรหัสผ่านให้ผู้ใช้ (ผู้ใช้ต้องเปลี่ยนในการเข้าสู่ระบบครั้งถัดไป)",
          },
          {
            name: "user.mfa_enabled / user.mfa_disabled / user.mfa_reset",
            description: "เปิด / ปิดการยืนยันสองขั้นตอน หรือผู้ดูแลรีเซ็ตให้",
          },
          { name: "user.email_verified", description: "ยืนยันอีเมลแล้ว" },
          {
            name: "user.identifier_added / _removed / _verified, user.primary_identifier_changed",
            description:
              "เพิ่ม / ลบ / ยืนยันอีเมล เบอร์โทร รหัสผู้ใช้งาน หรือชื่อผู้ใช้เพิ่มเติม และเปลี่ยนรายการหลัก",
          },
          { name: "session.revoked", description: "เพิกถอนเซสชัน (ผู้ใช้ถูกออกจากระบบ) data บอกจำนวนเซสชัน" },
        ],
      },
      {
        id: "event-types-admin",
        heading: "เหตุการณ์ของแอป การตั้งค่า และระบบ",
        fields: [
          {
            name: "client.registered / client.updated",
            description:
              "ลงทะเบียน / แก้ไขแอปพลิเคชัน (data บอก Client ID และช่องที่แก้ เช่น access, branding)",
          },
          { name: "client.secret_rotated", description: "สร้าง client secret ใหม่" },
          { name: "client.disabled / client.enabled", description: "ปิด / เปิดใช้งานแอปพลิเคชัน" },
          {
            name: "settings.updated",
            description:
              "แก้ไขการตั้งค่า (data.key บอกว่าส่วนไหน เช่น branding, log-retention, login-methods)",
          },
          {
            name: "role.* / group.* / user_type.*",
            description: "สร้าง (created) แก้ไข (updated) หรือลบ (deleted) บทบาท กลุ่ม หรือประเภทผู้ใช้",
          },
          {
            name: "data.exported / data.imported",
            description: "ส่งออก / นำเข้าข้อมูล (รวมถึงการส่งออกข้อมูลรายบุคคลตาม PDPA)",
          },
          {
            name: "root.*",
            description:
              "เหตุการณ์ของบัญชีระบบ root เช่น root.initial_password_viewed (มีคนเปิดดูรหัสผ่านเริ่มต้น) และ root.password_reset — ควรตรวจสอบทุกครั้งที่พบ",
          },
        ],
        tips: [
          "เหตุการณ์ที่ควรเฝ้าระวัง: user.sign_in_failed จำนวนมากจาก IP เดียว, root.*, user.roles_changed, client.secret_rotated และ data.exported",
        ],
      },
    ],
    related: ["log-retention", "user-detail", "users", "client-detail"],
  },
  en: {
    id: "audit-logs",
    title: "Audit logs",
    summary:
      "The history of every important event: who did what, to whom, when and from where — user sign-ins as well as every change made by administrators. Use it for reviews, investigations and as evidence.",
    access:
      "Needs logs:read (the owner, admin and auditor roles). The page is read-only: nobody can edit or delete single entries. Old entries are removed automatically after the period set under “Log retention”.",
    shot: { file: "audit-logs-list.png", caption: "The Audit logs page" },
    sections: [
      {
        id: "table",
        heading: "The events table",
        body: [
          "Newest first, 25 / 50 / 100 rows per page (choose under “Rows per page”). Use next / previous page to move through it; the reload button (circular arrow) fetches the latest events.",
          "There is no free-text search on this table — use “Filters” instead (next section) — and columns cannot be sorted.",
        ],
        fieldsTitle: "Columns in the table",
        fields: [
          { name: "Time", description: "When the event happened, in the platform's time zone." },
          { name: "Event", description: "The event type, e.g. user.signed_in (see “Common event types”)." },
          {
            name: "Actor",
            description:
              "Who caused it: the user ID (UUID) of the administrator or the user themself; “system” = the platform or a server command; “anonymous” = someone not signed in (e.g. a failed sign-in).",
          },
          {
            name: "Subject",
            description:
              "The user ID the event is about (e.g. the banned user). Empty when no particular user is affected.",
          },
          { name: "IP", description: "The IP address of the request, when known." },
          {
            name: "User agent",
            description: "The actor's browser / device. Hidden by default; show it with “Columns”.",
          },
        ],
      },
      {
        id: "details",
        heading: "Event details",
        shot: { file: "audit-logs-details.png", caption: "An expanded row with its details" },
        body: [
          "Click the “Show details” arrow at the start of a row to see everything recorded: the actor (type, ID, IP, browser), the subject user and the event data — e.g. the changed fields, the roles added / removed or the application's Client ID.",
          "Passwords, one-time codes, tokens and secrets are never logged. An email or phone number typed in a failed sign-in is stored as a hash (emailHash / valueHash), not the value itself.",
        ],
      },
      {
        id: "filters",
        heading: "Filtering events",
        shot: { file: "audit-logs-filter.png", caption: "The filter panel filtering by event type" },
        body: [
          "Click “Filters” in the toolbar, then “Add filter”, and choose the column, operator and value. Several filters are combined with “And” (all must match). The table returns to the first page whenever the filters change.",
        ],
        fieldsTitle: "Available filters",
        fields: [
          {
            name: "Event — is / is any of",
            description: "One event type, or several at once, e.g. user.banned and user.unbanned.",
          },
          {
            name: "Actor — equals",
            description: "Paste the actor's user ID (or system / anonymous); must match exactly.",
          },
          {
            name: "Subject — equals",
            description: "Paste the affected user's ID to see everything that happened to that user.",
          },
          {
            name: "Time — is on or after / is before",
            description: "A time range; use both filters for “from … before …”.",
          },
        ],
        steps: [
          "Example “who changed user roles this month”: click “Filters” → “Add filter”.",
          "Choose column “Event”, operator “is”, value user.roles_changed.",
          "Add another filter: column “Time”, operator “is on or after”, and pick the 1st of the month.",
          "Read the “Actor” column (the administrator) and expand a row to see the roles added / removed.",
        ],
        tips: [
          "A user's ID is in the address of their detail page; that page also has the user's own event history.",
          "“Remove all” in the filter panel clears every filter.",
        ],
      },
      {
        id: "export",
        heading: "Export to CSV",
        body: [
          "Click “Export” → “Download as CSV”. It downloads every event that matches the current filters (not just the visible page), up to 5,000 entries, as audit-logs-YYYY-MM-DD.csv — open it in Excel or Google Sheets.",
          "File columns: occurredAt (UTC time), type, actorType, actorId, subjectUserId, ip, userAgent, data.",
        ],
        tips: [
          "More than 5,000 events? Split the period with “Time” filters and export each part.",
          "Times in the file are UTC (Thailand = UTC + 7 hours).",
        ],
        warnings: [
          "The export contains user IDs and IP addresses, which are personal data — store and share it carefully (PDPA).",
        ],
      },
      {
        id: "event-types",
        heading: "Common event types",
        body: ["Event names read “thing.action”. The most common ones:"],
        fieldsTitle: "Users and sign-in",
        fields: [
          {
            name: "user.signed_in",
            description:
              "Successful sign-in (data shows the method — password / phone_otp / social — and whether 2FA was used).",
          },
          {
            name: "user.sign_in_failed",
            description: "Failed sign-in (e.g. wrong password); the actor is anonymous.",
          },
          { name: "user.signed_out", description: "Sign-out (session = one device, all = every device)." },
          {
            name: "user.created / user.updated / user.deleted",
            description: "An account was created / edited / deleted (user.updated lists the changed fields).",
          },
          { name: "user.banned / user.unbanned", description: "An account was banned / unbanned." },
          { name: "user.roles_changed", description: "Console roles were added or removed." },
          {
            name: "user.type_changed / user.groups_changed",
            description: "The user type / group memberships changed.",
          },
          {
            name: "user.password_changed",
            description: "The user changed their password, or reset it through an emailed link.",
          },
          {
            name: "user.password_reset_requested",
            description: "A password reset link was requested (by the user or an administrator).",
          },
          {
            name: "user.password_set_by_admin",
            description:
              "An administrator set the user's password (they must change it at the next sign-in).",
          },
          {
            name: "user.mfa_enabled / user.mfa_disabled / user.mfa_reset",
            description: "Two-step verification turned on / off, or reset by an administrator.",
          },
          { name: "user.email_verified", description: "An email address was verified." },
          {
            name: "user.identifier_added / _removed / _verified, user.primary_identifier_changed",
            description:
              "An extra email, phone, user code or username was added / removed / verified, or made primary.",
          },
          {
            name: "session.revoked",
            description: "Sessions were revoked (the user was signed out); data has the count.",
          },
        ],
      },
      {
        id: "event-types-admin",
        heading: "Application, settings and system events",
        fields: [
          {
            name: "client.registered / client.updated",
            description:
              "An application was registered / changed (data has the Client ID and changed fields, e.g. access, branding).",
          },
          { name: "client.secret_rotated", description: "A new client secret was generated." },
          { name: "client.disabled / client.enabled", description: "An application was disabled / enabled." },
          {
            name: "settings.updated",
            description: "A setting changed (data.key says which: branding, log-retention, login-methods …).",
          },
          {
            name: "role.* / group.* / user_type.*",
            description: "A role, group or user type was created, updated or deleted.",
          },
          {
            name: "data.exported / data.imported",
            description: "Data was exported / imported (including per-person PDPA exports).",
          },
          {
            name: "root.*",
            description:
              "Events of the root system account, e.g. root.initial_password_viewed (someone viewed the initial password) and root.password_reset — review each one.",
          },
        ],
        tips: [
          "Worth watching: many user.sign_in_failed from one IP, root.*, user.roles_changed, client.secret_rotated and data.exported.",
        ],
      },
    ],
    related: ["log-retention", "user-detail", "users", "client-detail"],
  },
};

export default topic;
